import { TextField, MenuItem, Avatar, SxProps } from '@mui/material';
import { useIsDesktop } from 'Hooks';
import { AccordionArrowPurple } from 'Images';
import { ReactNode } from 'react';

interface IControlledSelectInput2 {
  options: string[];
  value: string;
  width?: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
  ) => void;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
  ) => void;
  ariaLabelledById?: string;
  ariaLabel?: string;
  ariaRequired?: boolean;
  label?: string;
  sx?: SxProps;
  divWrapperSx?: any;
  size: 'small' | 'medium';
  id?: string;
  endAdornment?: ReactNode;
  iconSrc?: string;
  widthOffset?: string;
  disablePortal?: boolean;
  disabled?: boolean;
  onOpen?: () => void;
}

export const ControlledSelectInput2 = ({
  onChange,
  options,
  value,
  width,
  onFocus,
  onBlur,
  ariaLabelledById,
  ariaLabel,
  ariaRequired,
  label,
  sx,
  divWrapperSx,
  endAdornment,
  iconSrc = AccordionArrowPurple,
  size,
  id,
  widthOffset,
  disablePortal,
  disabled,
  onOpen,
}: IControlledSelectInput2) => {
  const isDesktop = useIsDesktop();
  return (
    <TextField
      disabled={disabled}
      id={id?.trim().replace(' ', '-')}
      value={value}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={onChange}
      label={label}
      aria-labelledby={ariaLabelledById ? ariaLabelledById : undefined}
      select
      fullWidth={!width}
      size={size}
      sx={{
        width: width,
        borderRadius: '8px',
        bgcolor: '#fff',

        '& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '8px',
          borderColor: 'primary.shade_200',
          borderWidth: '1px',
          '& legend': {
            textAlign: 'right',
            direction: 'rtl',
            '& span': {
              paddingLeft: '5px',
              paddingRight: '5px',
            },
          },
        },
        '.MuiInputLabel-root': {
          fontWeight: 400,
          right: '12px',
          left: 'auto',
          transformOrigin: 'top right',
        },
        '&&& .MuiInputLabel-root.MuiInputLabel-shrink': {
          right: 0,
          transform: 'translate(-14px, -9px) scale(0.75)',
        },
        '.MuiOutlinedInput-root': {
          height: '46px',
          fontWeight: 400,

          '&:hover fieldset': {
            borderColor: 'primary.main',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
            borderWidth: '1px',
          },
          '& .MuiSelect-select': {
            pl: endAdornment ? '64px' : '40px',
            pr: '14px !important',
          },
        },
        '& .MuiSelect-icon': {
          cursor: 'pointer',
          top: 'auto',
          right: 'auto',
          left: '8px',
        },
        '& .MuiInputAdornment-positionEnd': {
          position: 'absolute',
          left: '30px',
        },
        '.MuiSelect-iconOpen': {
          top: 'auto',
          right: 'auto',
          left: '8px',
        },
        '&.MuiFormControl-root': {
          height: '46px',
          '& div': {
            ...divWrapperSx,
          },
        },
        '& .MuiFormLabel-root[data-shrink="false"]': {
          pr: '14px',
        },
        ...sx,
      }}
      slotProps={{
        input: {
          endAdornment: endAdornment,
        },

        htmlInput: {
          'aria-label': ariaLabel,
          'aria-required': ariaRequired,
        },

        select: {
          onOpen,
          MenuProps: {
            disablePortal: disablePortal,
            PaperProps: {
              sx: {
                mt: isDesktop ? undefined : '2px',
                width: width,
                marginRight: widthOffset,
                padding: 0,
                color: 'text.secondary',
                borderRadius: '0 0px 8px 8px',
                boxShadow: '0px 6px 12px 0px rgba(21, 82, 232, 0.06)',
                maxHeight: '200px',
                '& ul': {
                  p: 0,
                },
              },
            },
          },
          IconComponent: (props) => (
            <Avatar
              {...props}
              src={iconSrc}
              alt={''}
              sx={{ width: '24px', height: '24px' }}
            />
          ),
        },
      }}
    >
      {options.map((value, index) => (
        <MenuItem
          key={index}
          value={value}
          sx={{
            fontSize: '18px !important',
            fontWeight: '400 !important',
            padding: '14px 20px 14px',
            '&:hover': {
              fontWeight: '700 !important',
              bgcolor: 'primary.shade_100',
            },
            '&.Mui-selected': {
              fontWeight: '700 !important',
              bgcolor: 'primary.shade_100',
            },
          }}
        >
          {value}
        </MenuItem>
      ))}
    </TextField>
  );
};
